import React from 'react';
import { Phone } from 'lucide-react';
import { Input } from '../../ui/input';
import { AddressAutocomplete } from '../../ui/AddressAutocomplete';
import { OnboardingFormData, US_STATES } from '../../../types/business';

interface StepAddressProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

export function StepAddress({ formData, updateField, error }: StepAddressProps) {
  const updateAddress = (field: string, value: string) => {
    updateField('address', { ...formData.address, [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Where is your boutique?
        </h2>
        <p className="text-body mt-1">
          This is used as the courier pickup location for deliveries.
        </p>
      </div>

      {/* Address Fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-heading">
            Street Address <span className="text-error">*</span>
          </label>
          <AddressAutocomplete
            value={formData.address.street}
            onChange={(val) => updateAddress('street', val)}
            onSelect={(parsed) =>
              updateField('address', {
                ...formData.address,
                street: parsed.street,
                city: parsed.city,
                state: parsed.state,
                zipCode: parsed.zipCode,
                lat: parsed.lat,
                lng: parsed.lng,
              })
            }
            placeholder="Start typing an address..."
          />
          <p className="text-xs text-body">Select a suggestion or type manually</p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-heading">
            Suite / Unit <span className="text-body font-normal">(optional)</span>
          </label>
          <Input
            type="text"
            placeholder="Suite 200"
            value={formData.address.unit}
            onChange={(e) => updateAddress('unit', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-heading">
              City <span className="text-error">*</span>
            </label>
            <Input
              type="text"
              placeholder="Los Angeles"
              value={formData.address.city}
              onChange={(e) => updateAddress('city', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-heading">
              State <span className="text-error">*</span>
            </label>
            <select
              value={formData.address.state}
              onChange={(e) => updateAddress('state', e.target.value)}
              className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans"
            >
              <option value="">Select state</option>
              {US_STATES.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="w-1/2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-heading">
              ZIP Code <span className="text-error">*</span>
            </label>
            <Input
              type="text"
              placeholder="90210"
              value={formData.address.zipCode}
              onChange={(e) => updateAddress('zipCode', e.target.value.replace(/[^\d-]/g, ''))}
              maxLength={10}
            />
          </div>
        </div>
      </div>

      {/* Phone Number */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-heading">
            Store Phone Number <span className="text-error">*</span>
          </label>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <Input
              type="tel"
              placeholder="(404) 555-1234"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>
          <p className="text-xs text-body">
            Used by delivery couriers to contact you at pickup. This won't be shown to customers.
          </p>
        </div>
      </div>
    </div>
  );
}
